import React, { useRef, useImperativeHandle, forwardRef, useState } from 'react';
import { useNotify, useGetAllowedValues } from 'hooks';
import { useUploadScorecardTemplate } from 'hooks/api/scorecard';
import { InputNameDialog, SelectionDialog } from 'components';

interface UploadTemplateProps {
    // siteId is not used in the current implementation
}

export interface UploadTemplateRef {
    triggerFileUpload: () => void;
}

export const UploadTemplate = forwardRef<UploadTemplateRef, UploadTemplateProps>((props, ref) => {
    const notify = useNotify();
    const { data } = useGetAllowedValues();
    const sites = data?.sites;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isTemplateNameDialogOpen, setIsTemplateNameDialogOpen] = useState(false);
    const [isSiteSelectionDialogOpen, setIsSiteSelectionDialogOpen] = useState(false);
    const [templateName, setTemplateName] = useState('');

    const uploadTemplate = useUploadScorecardTemplate();

    useImperativeHandle(ref, () => ({
        triggerFileUpload: () => {
            fileInputRef.current?.click();
        }
    }));

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.name.endsWith('.xlsx')) {
                notify.error('Please select an Excel (.xlsx) file.');
                return;
            }

            setSelectedFile(file);
            setIsTemplateNameDialogOpen(true);

            // Reset file input
            if (event.target) {
                // eslint-disable-next-line no-param-reassign
                event.target.value = '';
            }
        }
    };

    const handleTemplateNameSubmit = (name: string) => {
        setTemplateName(name);
        setIsTemplateNameDialogOpen(false);
        setIsSiteSelectionDialogOpen(true);
    };

    const handleSitesSelect = (siteIds: number[]) => {
        if (selectedFile && templateName) {
            uploadTemplate.mutate({
                siteIds,
                file: selectedFile,
                templateName
            }, {
                onSuccess: () => {
                    notify.success('Template uploaded successfully');
                    setSelectedFile(null);
                    setTemplateName('');
                },
                onError: (error) => {
                    notify.error(`Failed to upload template: ${error.message}`);
                }
            });
        }
    };

    const handleTemplateNameDialogClose = () => {
        setIsTemplateNameDialogOpen(false);
        setSelectedFile(null);
    };

    const handleSiteSelectionDialogClose = () => {
        setIsSiteSelectionDialogOpen(false);
        setSelectedFile(null);
        setTemplateName('');
    };

    const handleBackButtonClick = () => {
        setIsSiteSelectionDialogOpen(false);
        setIsTemplateNameDialogOpen(true);
    };

    return (
        <>
            <input
                type="file"
                accept=".xlsx"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
                data-testid="file-input"
            />

            <InputNameDialog
                isOpen={isTemplateNameDialogOpen}
                onClose={handleTemplateNameDialogClose}
                onSubmit={handleTemplateNameSubmit}
                dialogTitle='Step 1/2'
                mainTitle="Create Template from Excel"
                placeholder="Enter template name"
                label="Template Name"
                submitButtonText="Next"
                data-testid="input-name-dialog"
            />

            <SelectionDialog
                options={(sites || []).map(site => ({ id: site.siteId, name: site.siteName }))}
                isDialogOpen={isSiteSelectionDialogOpen}
                onDialogClose={handleSiteSelectionDialogClose}
                onMultiSelect={handleSitesSelect}
                multiSelect
                dialogTitle='Step 2/2'
                mainTitle="Assign Template to Sites"
                submitButtonText="Confirm"
                cancelButtonText="Back"
                onCancel={handleBackButtonClick}
                data-testid="selection-dialog"
            />
        </>
    );
});
